import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2, MapPin, MessageCircle, Phone, Search, Store } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BranchPopup from "../components/BranchPopup";
import api, { API_BASE } from "../lib/api";

const photoUrl = (b) => `${API_BASE}/api/branches/public/photo/${b.id}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function BranchCard({ b, onOpen, i }) {
  const wa = `https://wa.me/91${b.phone}?text=${encodeURIComponent(`Namaste! I found your INDIAFAST branch "${b.shop_name}" on the website. I want to ship a parcel.`)}`;
  const stop = (e) => e.stopPropagation();
  return (
    <div onClick={() => onOpen(b)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(b)}
         className="group cursor-pointer overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-saffron/40 hover:shadow-2xl hover:shadow-stone-200"
         data-testid={`branch-card-${b.id}`}
         style={{ animation: `fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${Math.min(i, 8) * 60}ms both` }}>
      <div className="relative h-44 overflow-hidden bg-navy">
        {b.photo_path ? (
          <img src={photoUrl(b)} alt={b.shop_name} loading="lazy"
               className="kenburns-a h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid h-full place-items-center bg-gradient-to-br from-navy to-navy-soft">
            <Store className="h-10 w-10 text-white/25" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
        <span className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-saffron via-white to-emerald-600" />
        <span className="stamp-badge absolute left-3 top-3 -rotate-2 rounded-sm px-3 py-1.5" data-testid={`branch-stamp-${b.id}`}>
          <span className="block text-[7px] font-bold uppercase tracking-[0.25em] text-slate-400">IndiaFast · India</span>
          <span className="block font-heading text-sm font-extrabold uppercase tracking-wide text-navy">{b.city}</span>
          {b.district ? <span className="block text-[8px] font-bold uppercase tracking-wider text-saffron">{b.district}</span> : null}
        </span>
        {b.pincode && (
          <span className="absolute right-3 top-3 rounded-full bg-saffron px-2.5 py-1 text-[10px] font-bold text-white shadow-lg shadow-saffron/40">
            {b.pincode}
          </span>
        )}
        <span className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-saffron text-white opacity-0 shadow-xl transition-all duration-300 group-hover:opacity-100">
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
      <div className="p-5">
        <h4 className="font-heading text-base font-bold text-navy transition-colors group-hover:text-saffron">{b.shop_name}</h4>
        <p className="mt-0.5 text-xs font-semibold text-saffron">{b.agent_name}</p>
        <p className="mt-2 line-clamp-2 min-h-[32px] text-xs leading-relaxed text-slate-500">{b.address}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={`tel:+91${b.phone}`} onClick={stop} data-testid={`branch-call-${b.id}`}
             className="flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-2 text-[11px] font-bold text-white transition-colors hover:bg-saffron">
            <Phone className="h-3.5 w-3.5" /> Call
          </a>
          <a href={wa} target="_blank" rel="noreferrer" onClick={stop} data-testid={`branch-wa-${b.id}`}
             className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3.5 py-2 text-[11px] font-bold text-white transition-colors hover:bg-emerald-500">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
          {b.lat != null && b.lng != null && (
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}`} target="_blank" rel="noreferrer" onClick={stop} data-testid={`branch-directions-${b.id}`}
               className="flex items-center gap-1.5 rounded-full border border-saffron/40 bg-saffron/10 px-3.5 py-2 text-[11px] font-bold text-saffron transition-colors hover:bg-saffron hover:text-white">
              <MapPin className="h-3.5 w-3.5" /> Directions
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [state, setState] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/branches/public").then((r) => setBranches(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
  }, []);

  const states = useMemo(() => {
    const m = {};
    for (const b of branches) m[b.state] = (m[b.state] || 0) + 1;
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [branches]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return branches.filter((b) => {
      if (state !== "All" && b.state !== state) return false;
      if (!q) return true;
      return [b.state, b.district, b.city, b.shop_name, b.agent_name, b.address, b.pincode]
        .some((f) => (f || "").toLowerCase().includes(q));
    });
  }, [branches, query, state]);

  return (
    <div className="min-h-screen bg-[#F4F3F1]" data-testid="branches-page">
      <Navbar />
      <section className="relative overflow-hidden bg-navy pb-16 pt-28">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-saffron/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-emerald-600/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-semibold text-slate-300">
            <span className="h-2 w-2 rounded-full bg-saffron" /> Our Network
          </p>
          <h1 className="mt-3 font-heading text-3xl font-medium tracking-tight text-white sm:text-5xl">
            Find an IndiaFast branch <span className="text-saffron">near you.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Walk in, call, or WhatsApp our partner agents — {branches.length ? `${branches.length} branches across ${states.length} states.` : "across India."}
          </p>
          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="branches-search"
                   placeholder="Search state, district, city, pincode or shop…"
                   className="w-full rounded-full border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder-slate-400 outline-none backdrop-blur focus:border-saffron" />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading branches…</div>
        ) : !branches.length ? (
          <div className="rounded-3xl border border-stone-200 bg-white p-14 text-center" data-testid="branches-empty">
            <Store className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-navy">Branch network launching soon</p>
            <p className="mt-1 text-xs text-slate-500">New partner branches are being onboarded.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="branch-state-filters">
              <button onClick={() => setState("All")} data-testid="branch-filter-all"
                      className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${state === "All" ? "bg-navy text-white" : "border border-stone-300 bg-white text-slate-600 hover:border-saffron hover:text-saffron"}`}>
                All ({branches.length})
              </button>
              {states.map(([s, n]) => (
                <button key={s} onClick={() => setState(s)} data-testid={`branch-filter-${slug(s)}`}
                        className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${state === s ? "bg-saffron text-white" : "border border-stone-300 bg-white text-slate-600 hover:border-saffron hover:text-saffron"}`}>
                  {s} ({n})
                </button>
              ))}
            </div>

            {!filtered.length ? (
              <div className="mt-8 rounded-3xl border border-stone-200 bg-white p-14 text-center" data-testid="branches-no-match">
                <Store className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm font-semibold text-navy">No branches match your search</p>
                <p className="mt-1 text-xs text-slate-500">Try a different state, district or city name.</p>
              </div>
            ) : (
              <>
                <p className="mt-6 text-xs font-semibold text-slate-500" data-testid="branches-count">
                  Showing {filtered.length} branch{filtered.length === 1 ? "" : "es"}{state !== "All" ? ` in ${state}` : ""}
                </p>
                <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="branches-grid">
                  {filtered.map((b, i) => <BranchCard key={b.id} b={b} i={i} onOpen={setSelected} />)}
                </div>
              </>
            )}
          </>
        )}
      </main>
      <BranchPopup b={selected} onClose={() => setSelected(null)} />
      <Footer contact={contact} />
    </div>
  );
}
